/**
 * QR Code Detection Utility for QuickGrade Worksheets
 *
 * Detects QR codes in scanned worksheet images and parses the
 * worksheet identification format: QG:worksheet_id:page_number
 *
 * Handles rotated/skewed scans by attempting detection at multiple orientations.
 */

import jsQR from "jsqr";

// ============================================================================
// Types
// ============================================================================

/** Result of QR code detection and parsing */
export interface QRDetectionResult {
  /** Whether a valid QR code was detected */
  success: boolean;
  /** Worksheet ID extracted from QR code */
  worksheetId: string | null;
  /** Page number extracted from QR code (1-indexed) */
  pageNumber: number | null;
  /** Raw QR code content (for debugging) */
  rawContent: string | null;
  /** Detection confidence score (0-1) based on rotation needed */
  confidence: number;
  /** Rotation angle where QR was found (0, 90, 180, 270) */
  rotationAngle: number;
  /** Error message if detection failed */
  error: string | null;
}

/** Options for QR detection */
export interface QRDetectionOptions {
  /** Whether to try multiple rotations (default: true) */
  tryRotations?: boolean;
  /** Target resolution for processing (default: 1200) */
  targetResolution?: number;
  /** Region of interest: 'bottom-right' | 'full' (default: 'bottom-right') */
  scanRegion?: "bottom-right" | "full";
}

// ============================================================================
// Constants
// ============================================================================

/** QR code format prefix */
const QR_PREFIX = "QG:";

/** Rotation angles to attempt for detection */
const ROTATION_ANGLES = [0, 90, 180, 270];

/** Confidence scores for each rotation (prefer 0 rotation) */
const ROTATION_CONFIDENCE: Record<number, number> = {
  0: 1.0,
  90: 0.85,
  180: 0.7,
  270: 0.85,
};

/** Default processing resolution */
const DEFAULT_RESOLUTION = 1200;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Load an image from a File or blob URL into an HTMLImageElement
 */
async function loadImage(source: File | string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));

    if (source instanceof File) {
      img.src = URL.createObjectURL(source);
    } else {
      img.src = source;
    }
  });
}

/**
 * Extract image data from a canvas region
 */
function getImageDataFromCanvas(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number
): ImageData {
  return ctx.getImageData(x, y, width, height);
}

/**
 * Draw image to canvas at specified rotation
 */
function drawRotatedImage(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  angle: number,
  targetSize: number
): { width: number; height: number } {
  // Calculate scaled dimensions
  const scale = Math.min(targetSize / img.width, targetSize / img.height);
  const scaledWidth = Math.floor(img.width * scale);
  const scaledHeight = Math.floor(img.height * scale);

  // For 90/270 rotation, swap canvas dimensions
  const isRotated90or270 = angle === 90 || angle === 270;
  const canvasWidth = isRotated90or270 ? scaledHeight : scaledWidth;
  const canvasHeight = isRotated90or270 ? scaledWidth : scaledHeight;

  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  // Clear and set white background (important for QR detection)
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  // Apply rotation transform
  ctx.save();
  ctx.translate(canvasWidth / 2, canvasHeight / 2);
  ctx.rotate((angle * Math.PI) / 180);

  // Draw centered image
  const drawX = -scaledWidth / 2;
  const drawY = -scaledHeight / 2;
  ctx.drawImage(img, drawX, drawY, scaledWidth, scaledHeight);
  ctx.restore();

  return { width: canvasWidth, height: canvasHeight };
}

/**
 * Extract the bottom-right quadrant of the image (where QR codes are placed)
 */
function getBottomRightRegion(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number
): ImageData {
  // QR code is typically in bottom-right ~20% of page
  // Use a slightly larger region (25%) for safety with skewed scans
  const regionWidth = Math.floor(width * 0.3);
  const regionHeight = Math.floor(height * 0.25);
  const x = width - regionWidth;
  const y = height - regionHeight;

  return ctx.getImageData(x, y, regionWidth, regionHeight);
}

/**
 * Parse QR content in QuickGrade format
 * Format: QG:worksheet_id:page_number
 */
function parseQRContent(content: string): {
  worksheetId: string;
  pageNumber: number;
} | null {
  if (!content.startsWith(QR_PREFIX)) {
    return null;
  }

  const parts = content.split(":");
  if (parts.length !== 3) {
    return null;
  }

  const worksheetId = parts[1];
  const pageNumber = parseInt(parts[2], 10);

  if (!worksheetId || isNaN(pageNumber) || pageNumber < 1) {
    return null;
  }

  return { worksheetId, pageNumber };
}

/**
 * Attempt QR detection on ImageData
 */
function detectQRInImageData(imageData: ImageData): string | null {
  const code = jsQR(imageData.data, imageData.width, imageData.height, {
    inversionAttempts: "dontInvert",
  });

  return code?.data || null;
}

// ============================================================================
// Main Detection Function
// ============================================================================

/**
 * Detect QR code in an image and parse worksheet identification
 *
 * @param source - Image File or preview URL
 * @param options - Detection options
 * @returns QR detection result with worksheet ID and page number
 *
 * @example
 * ```typescript
 * const result = await detectQRCode(scannedImage.file);
 *
 * if (result.success) {
 *   console.log(`Worksheet: ${result.worksheetId}, Page: ${result.pageNumber}`);
 * } else {
 *   console.log(`Detection failed: ${result.error}`);
 * }
 * ```
 */
export async function detectQRCode(
  source: File | string,
  options: QRDetectionOptions = {}
): Promise<QRDetectionResult> {
  const {
    tryRotations = true,
    targetResolution = DEFAULT_RESOLUTION,
    scanRegion = "bottom-right",
  } = options;

  // Default failed result
  const failedResult: QRDetectionResult = {
    success: false,
    worksheetId: null,
    pageNumber: null,
    rawContent: null,
    confidence: 0,
    rotationAngle: 0,
    error: null,
  };

  try {
    // Load image
    const img = await loadImage(source);

    // Create canvas for processing
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    if (!ctx) {
      return {
        ...failedResult,
        error: "Failed to create canvas context",
      };
    }

    // Determine rotation angles to try
    const anglesToTry = tryRotations ? ROTATION_ANGLES : [0];

    // Try each rotation
    for (const angle of anglesToTry) {
      const { width, height } = drawRotatedImage(
        canvas,
        ctx,
        img,
        angle,
        targetResolution
      );

      // Get image data from appropriate region
      let imageData: ImageData;
      if (scanRegion === "bottom-right") {
        imageData = getBottomRightRegion(ctx, width, height);
      } else {
        imageData = getImageDataFromCanvas(canvas, ctx, 0, 0, width, height);
      }

      // Attempt detection
      let qrContent = detectQRInImageData(imageData);

      // If not found in bottom-right, try full image (QR might be elsewhere)
      if (!qrContent && scanRegion === "bottom-right") {
        const fullImageData = ctx.getImageData(0, 0, width, height);
        qrContent = detectQRInImageData(fullImageData);
      }

      if (qrContent) {
        // Parse the QR content
        const parsed = parseQRContent(qrContent);

        if (parsed) {
          // Success!
          // Cleanup URL if we created one
          if (source instanceof File) {
            // Note: We don't revoke here as the image might be reused
            // Caller is responsible for cleanup
          }

          return {
            success: true,
            worksheetId: parsed.worksheetId,
            pageNumber: parsed.pageNumber,
            rawContent: qrContent,
            confidence: ROTATION_CONFIDENCE[angle] || 0.5,
            rotationAngle: angle,
            error: null,
          };
        } else {
          // QR found but not in expected format
          return {
            ...failedResult,
            rawContent: qrContent,
            rotationAngle: angle,
            error: `QR code found but format invalid: "${qrContent}". Expected format: QG:worksheet_id:page_number`,
          };
        }
      }
    }

    // No QR code found at any rotation
    return {
      ...failedResult,
      error:
        "No QR code detected. Please ensure the worksheet QR code is visible and try again.",
    };
  } catch (err) {
    return {
      ...failedResult,
      error:
        err instanceof Error ? err.message : "Unknown error during QR detection",
    };
  }
}

/**
 * Detect QR codes in multiple images (batch processing)
 *
 * @param sources - Array of image Files or preview URLs
 * @param options - Detection options
 * @returns Array of detection results
 */
export async function detectQRCodeBatch(
  sources: (File | string)[],
  options: QRDetectionOptions = {}
): Promise<QRDetectionResult[]> {
  const results: QRDetectionResult[] = [];

  for (const source of sources) {
    const result = await detectQRCode(source, options);
    results.push(result);
  }

  return results;
}

/**
 * Check if all scanned images belong to the same worksheet
 *
 * @param results - Array of QR detection results
 * @returns Object with validation result
 */
export function validateBatchWorksheet(results: QRDetectionResult[]): {
  valid: boolean;
  worksheetId: string | null;
  pageNumbers: number[];
  errors: string[];
} {
  const errors: string[] = [];
  const pageNumbers: number[] = [];
  let worksheetId: string | null = null;

  for (let i = 0; i < results.length; i++) {
    const result = results[i];

    if (!result.success) {
      errors.push(`Page ${i + 1}: ${result.error || "QR detection failed"}`);
      continue;
    }

    // Check worksheet ID consistency
    if (worksheetId === null) {
      worksheetId = result.worksheetId;
    } else if (result.worksheetId !== worksheetId) {
      errors.push(
        `Page ${i + 1}: Worksheet mismatch. Expected "${worksheetId}", got "${result.worksheetId}"`
      );
    }

    if (result.pageNumber !== null) {
      pageNumbers.push(result.pageNumber);
    }
  }

  // Check for duplicate page numbers
  const uniquePages = new Set(pageNumbers);
  if (uniquePages.size !== pageNumbers.length) {
    errors.push("Duplicate page numbers detected");
  }

  return {
    valid: errors.length === 0 && worksheetId !== null,
    worksheetId,
    pageNumbers: [...uniquePages].sort((a, b) => a - b),
    errors,
  };
}
